import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getProducts } from '../services/productService';
import * as wishlistService from '../services/wishlistService';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import toast from 'react-hot-toast';
import FilterSidebar from './FilterSidebar';
import getOptimizedUrl from '../utils/imageUtils';

// --- Price Formatting Helper ---
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(price);
};

// --- Arrow SVG Components ---
const ArrowLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);
const ArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

// --- Main Component ---
const ProductDisplay = ({isStore = false}) => {
  const navigate = useNavigate();
  const [view, setView] = useState('grid');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [wishlistIds, setWishlistIds] = useState([]);
  const lastNavigateAtRef = useRef(0);
  const lastWishlistTapAtRef = useRef(0);
  
  // Responsive State
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12; 

  const [products, setProducts] = useState([]);

  const notifyHomeContentUpdated = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('home:content-updated'));
    }
  };

  // Filter states
  const [appliedFilters, setAppliedFilters] = useState({
    categories: [],
    colours: [],
    sleeves: [],
    length: [],
    print: [],
    pattern: [],
    material: [],
    priceRange: [0, 4999],
    discountRange: [0, 100],
    collection: []
  });

  // Track window width for responsive slider
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    
    const loadWishlist = async () => {
      const user = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (user && token) {
        try {
          const data = await wishlistService.getWishlist();
          if (mounted) {
            setWishlistIds(data.map(item => item.product._id));
          }
        } catch (error) {
          console.error('Failed to load wishlist:', error);
        }
      } else {
        if (mounted) setWishlistIds([]);
      }
    };
    
    loadWishlist();
    
    const shownInHome = !isStore;
    getProducts({ shownInHome })
      .then((data) => {
        if (!mounted) return;
        const productsData = Array.isArray(data) ? data : [];
        setProducts(productsData);
        notifyHomeContentUpdated();
        
        productsData.forEach((product) => {
          if (product.images && product.images[1]) {
            const img = new Image();
            img.src = product.images[1];
          }
        });
      })
      .catch((err) => {
        if (!mounted) return;
        console.error(err?.message || 'Failed to load products');
      })
      .finally(() => {
        if (!mounted) return;
        console.log('Products loaded');
        notifyHomeContentUpdated();
        setTimeout(notifyHomeContentUpdated, 150);
      });
    
    return () => { 
      mounted = false;
    };
  }, [isStore]);

  const location = useLocation();
  const [collectionFilter, setCollectionFilter] = useState(null);

  useEffect(() => {
    if (!isStore) return;
    const params = new URLSearchParams(location.search);
    const coll = params.get('collection');

    if (coll && coll !== 'All') {
      // For Roz Roz / Kuch Roz, just switch the tab instead of filtering
      if (coll === 'Roz Roz' || coll === 'Kuch Roz') {
        setSelectedCategory(coll);
        setCollectionFilter(null);
        setAppliedFilters(prev => ({ ...prev, categories: [], collection: [] }));
      } else {
        // For other collections (Tops, Bottoms, etc.), use collection filter
        setCollectionFilter(coll);
        setSelectedCategory('All');
        setAppliedFilters(prev => ({ ...prev, categories: [], collection: [] }));
      }
    } else {
      setCollectionFilter(null);
      setSelectedCategory('All');
      setAppliedFilters(prev => ({ ...prev, categories: [], collection: [] }));
    }
    setCurrentPage(1);
  }, [isStore, location.search]);

  const filteredProducts = selectedCategory === 'All'
    ? products
    : products.filter(product => product.category === selectedCategory);

  // Helper function to check if product matches collection filter (URL param)
  const matchesCollectionFilter = (product) => {
    if (!collectionFilter) return true;
    return product.storeCategory === collectionFilter;
  };

  const advancedFilteredProducts = filteredProducts.filter(product => {
    // First check collection filter
    if (!matchesCollectionFilter(product)) return false;

    if (appliedFilters.categories.length > 0 && !appliedFilters.categories.includes(product.storeCategory) && !appliedFilters.categories.includes(product.productType)) return false;
    if (appliedFilters.colours.length > 0 && !appliedFilters.colours.includes(product.color)) return false;
    if (appliedFilters.sleeves.length > 0 && !appliedFilters.sleeves.includes(product.sleeves)) return false;
    if (appliedFilters.length.length > 0 && !appliedFilters.length.includes(product.length)) return false;
    if (appliedFilters.print.length > 0 && !appliedFilters.print.includes(product.print)) return false;
    if (appliedFilters.pattern.length > 0 && !appliedFilters.pattern.includes(product.fabricPattern)) return false;

    // Check collection filter from sidebar (Roz Roz, Kuch Roz)
    if (appliedFilters.collection.length > 0 && !appliedFilters.collection.includes(product.category)) return false;

    if (appliedFilters.material.length > 0 && !appliedFilters.material.includes(product.fabricType)) return false;

    const productPrice = product.discountedPrice ?? product.mrp ?? product.price ?? 0;
    if (productPrice < appliedFilters.priceRange[0] || productPrice > appliedFilters.priceRange[1]) return false;

    if (product.mrp && product.discountedPrice) {
      const discountPercent = Math.round(((product.mrp - product.discountedPrice) / product.mrp) * 100);
      if (discountPercent < appliedFilters.discountRange[0]) return false;
    }

    return true;
  });

  const handleFilterChange = (newFilters) => {
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  const handleFilterOpen = () => {
    window.dispatchEvent(new CustomEvent('filter:toggle', { detail: { open: true } }));
  };

  const totalPages = Math.ceil(advancedFilteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = isStore ? advancedFilteredProducts.slice(startIndex, endIndex) : advancedFilteredProducts;

  // RESPONSIVE: 2.1 items on mobile, exactly 3 items on desktop
  const itemsPerView = windowWidth < 768 ? 2.1 : 3;
  const maxIndex = Math.max(0, advancedFilteredProducts.length - Math.floor(itemsPerView));

  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) goToPage(currentPage + 1);
  };

  const activateProduct = (productIdOrSlug) => {
    const now = Date.now();
    if (now - lastNavigateAtRef.current < 220) return;
    lastNavigateAtRef.current = now;
    navigate(`/product/${productIdOrSlug}`);
  };

  const activateWishlist = (productId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const now = Date.now();
    if (now - lastWishlistTapAtRef.current < 220) return;
    lastWishlistTapAtRef.current = now;
    toggleWishlist(productId, e);
  };

  const toggleWishlist = async (productId, sizeOrEvent, maybeEvent) => {
    // support both signatures: (productId, event) or (productId, size, event)
    let size = undefined;
    let e = maybeEvent;
    if (sizeOrEvent && typeof sizeOrEvent === 'object' && sizeOrEvent.nativeEvent) {
      e = sizeOrEvent;
    } else {
      size = sizeOrEvent;
    }
    if (e) e.stopPropagation();
    
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!user || !token) {
      toast.error('Please login to add items to wishlist');
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", {
            detail: {
              open: true,
              returnToCurrent: true,
              returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          })
        );
      } catch (err) {}
      return;
    }
    
    const isCurrentlyInWishlist = wishlistIds.includes(productId);
    
    try {
      if (isCurrentlyInWishlist) {
        await wishlistService.removeFromWishlist(productId, size);
        const newWishlistIds = wishlistIds.filter(id => id !== productId);
        setWishlistIds(newWishlistIds);
        toast.success('Removed from wishlist');
        const data = await wishlistService.getWishlist();
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: data } }));
      } else {
        await wishlistService.addToWishlist(productId, size);
        setWishlistIds([...wishlistIds, productId]);
        toast.success('Added to wishlist!');
        const data = await wishlistService.getWishlist();
        window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist: data } }));
      }
    } catch (error) {
      console.error('Wishlist error:', error);
      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) toast.error('Failed to update wishlist');
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 100);
    return () => clearTimeout(timer);
  }, [view]);

  return (
    <div className="w-full bg-white text-black font-sans flex flex-col">

      <FilterSidebar
        products={filteredProducts}
        onFilterChange={handleFilterChange}
        collectionFilter={collectionFilter}
        onClearCollectionFilter={() => setCollectionFilter(null)}
      />

      {/* --- Top Control Bar --- */}
      <div className="flex justify-between items-center py-4 px-4 md:px-6 sticky top-0 bg-white z-10">
        {/* Left side - Only show on store page */}
        {isStore ? (
          <div className="flex items-center space-x-0 overflow-x-auto no-scrollbar">
            <button
              onClick={handleFilterOpen}
              className="home-body-text font-semibold text-gray-400 hover:text-black transition-colors"
            >
              Filter
            </button>
          </div>
        ) : (
          <div></div>
        )}

        {/* Right side - Always show */}
        <div className="flex items-center space-x-2 md:space-x-4 flex-shrink-0">
          <button
            onClick={() => setView('grid')}
            className={`home-body-text text-sm ${view === 'grid' ? 'text-black font-semibold' : 'text-gray-400'} hover:text-black transition-colors`}
          >
            Image
          </button>
          <button
            onClick={() => setView('list')}
            className={`home-body-text text-sm ${view === 'list' ? 'text-black font-semibold' : 'text-gray-400'} hover:text-black transition-colors`}
          >
            List
          </button>
        </div>
      </div>
      
      {/* --- Products Area --- */}
      {view === 'grid' ? (
        !isStore ? (
          // --- Slider View (Home) ---
          // RESPONSIVE: itemsPerView handles the column count logic here
          <>
          <div className="overflow-x-auto md:overflow-hidden px-2 md:px-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div
              className="flex h-full snap-x snap-mandatory md:snap-none md:transition-transform md:duration-500 md:ease-in-out"
              style={{ transform: windowWidth >= 768 ? `translateX(-${currentIndex * (100 / itemsPerView)}%)` : 'none' }}
            >
              {paginatedProducts.map((product, index) => (
                <div
                  key={product._id || product.id}
                  className="snap-start flex-shrink-0 px-2 md:px-3 flex flex-col"
                  style={{ flexBasis: `${100 / itemsPerView}%` }}
                >
                  <div 
                    className="flex-grow w-full bg-white overflow-hidden flex items-center justify-center cursor-pointer relative aspect-[3/4] touch-manipulation"
                    onMouseEnter={() => {
                      if (windowWidth >= 768) setHoveredProduct(product._id || product.id);
                    }}
                    onMouseLeave={() => {
                      if (windowWidth >= 768) setHoveredProduct(null);
                    }}
                    onClick={() => activateProduct(product.slug || product._id || product.id)}
                  >
                    <img
                      src={getOptimizedUrl((product.images && product.images[0]) || product.imageUrl || '', 800)}
                      alt={product.product || product.name}
                      loading={index < (windowWidth < 768 ? 2 : 4) ? 'eager' : 'lazy'}
                      fetchPriority={index < (windowWidth < 768 ? 2 : 4) ? 'high' : 'auto'}
                      decoding="async"
                      className={`w-full h-full object-cover bg-white transition-opacity duration-300 ${
                        hoveredProduct === (product._id || product.id) && product.images && product.images[1] 
                          ? 'opacity-0' 
                          : 'opacity-100'
                      }`}
                    />
                    {product.images && product.images[1] && (
                      <img
                        src={getOptimizedUrl(product.images[1] || '', 800)}
                        alt={product.product || product.name}
                        loading="lazy"
                        decoding="async"
                        className={`w-full h-full object-cover bg-white transition-opacity duration-300 absolute inset-0 ${
                          hoveredProduct === (product._id || product.id) 
                            ? 'opacity-100' 
                            : 'opacity-0'
                        }`}
                      />
                    )}
                    <button
                      type="button"
                      aria-label={wishlistIds.includes(product._id || product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                      onPointerUp={(e) => activateWishlist(product._id || product.id, e)}
                      onClick={(e) => activateWishlist(product._id || product.id, e)}
                      className="absolute bottom-1 right-1 z-20 p-2 touch-manipulation"
                    >
                      {wishlistIds.includes(product._id || product.id) ? (
                        <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="white" stroke="black"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className="product-info mt-2 pb-6">
                    <h3 className="home-body-text text-sm md:text-base font-medium mb-3 ml-1 px-0 font-bdogrotesk text-left truncate">{product.product || product.name}</h3>
                    <button
                      type="button"
                      onClick={() => activateProduct(product.slug || product._id || product.id)}
                      className="group w-full bg-black text-white px-2 md:px-3 py-1.5 md:py-3 flex items-center justify-between font-medium tracking-wide font-bdogrotesk"
                    >
                      <div className="relative h-[14px] md:h-4 overflow-hidden">
                        <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                          <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none">Shop now</span>
                          <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none">Shop now</span>
                        </div>
                      </div>
                      <div className="relative h-[14px] md:h-4 overflow-hidden">
                        <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                          <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none text-white/90">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
                          <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none text-white/90">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>
        ) : (
          // --- Store Page Grid ---
          // RESPONSIVE CHANGE: grid-cols-2 on mobile, grid-cols-3 on desktop
          <>
          <div className="border-t-2 border-black mx-4 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-2 md:px-6">
            {paginatedProducts.map((product, index) => (
              <div key={product._id || product.id} className="flex flex-col">
                <div 
                  className="w-full bg-white overflow-hidden cursor-pointer relative aspect-[3/4] md:h-auto touch-manipulation"
                  onMouseEnter={() => {
                    if (windowWidth >= 768) setHoveredProduct(product._id || product.id);
                  }}
                  onMouseLeave={() => {
                    if (windowWidth >= 768) setHoveredProduct(null);
                  }}
                  onClick={() => activateProduct(product.slug || product._id || product.id)}
                >
                  <img 
                    src={getOptimizedUrl((product.images && product.images[0]) || product.imageUrl || '', 800)}
                    alt={product.product || product.name} 
                    loading={index < (windowWidth < 768 ? 2 : 4) ? 'eager' : 'lazy'}
                    fetchPriority={index < (windowWidth < 768 ? 2 : 4) ? 'high' : 'auto'}
                    decoding="async"
                    className={`w-full h-full object-cover bg-white transition-opacity duration-300 ${
                      hoveredProduct === (product._id || product.id) && product.images && product.images[1] 
                        ? 'opacity-0' 
                        : 'opacity-100'
                    }`}
                  />
                  {product.images && product.images[1] && (
                    <img 
                      src={getOptimizedUrl(product.images[1] || '', 800)}
                      alt={product.product || product.name} 
                      loading="lazy"
                      decoding="async"
                      className={`w-full h-full object-cover bg-white transition-opacity duration-300 absolute inset-0 ${
                        hoveredProduct === (product._id || product.id) 
                          ? 'opacity-100' 
                          : 'opacity-0'
                      }`}
                    />
                  )}
                  <button
                    type="button"
                    aria-label={wishlistIds.includes(product._id || product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    onPointerUp={(e) => activateWishlist(product._id || product.id, e)}
                    onClick={(e) => activateWishlist(product._id || product.id, e)}
                    className="absolute bottom-1 right-1 z-20 p-2 touch-manipulation"
                  >
                    {wishlistIds.includes(product._id || product.id) ? (
                      <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="white" stroke="black"/>
                      </svg>
                    )}
                  </button>
                </div>
                <div className="pb-2 pt-2 px-0 space-y-3">
                  <h3 className="home-body-text text-sm md:text-base font-medium ml-1 px-0 font-bdogrotesk text-left truncate">{product.product || product.name}</h3>
                  <button
                    type="button"
                    onClick={() => activateProduct(product.slug || product._id || product.id)}
                    className="group w-full bg-black text-white px-2 md:px-3 py-1.5 md:py-3 flex items-center justify-between font-medium tracking-wide font-bdogrotesk"
                  >
                    <div className="relative h-[14px] md:h-4 overflow-hidden">
                      <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                        <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none">Shop now</span>
                        <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none">Shop now</span>
                      </div>
                    </div>
                    <div className="relative h-[14px] md:h-4 overflow-hidden">
                      <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                        <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none text-white/90">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
                        <span className="flex h-[14px] md:h-4 items-center text-[11px] md:text-base leading-none text-white/90">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>
          </>
        )
      ) : (
        // --- List View (Unchanged layout, just ensuring responsiveness) ---
        <div className="divide-y divide-black border-t-2 border-b-2 border-black flex-grow mx-4 md:mx-6">
          {paginatedProducts.map((product) => (
            <div key={product._id || product.id} className="relative flex flex-col md:flex-row justify-between items-center md:items-stretch py-4 transition-colors min-h-[50vw] md:min-h-[45vw] lg:min-h-[20vw] gap-4">
              {isStore && (
                <button
                  type="button"
                  aria-label={wishlistIds.includes(product._id || product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  onPointerUp={(e) => activateWishlist(product._id || product.id, e)}
                  onClick={(e) => activateWishlist(product._id || product.id, e)}
                  className="hidden lg:block absolute top-2 right-2 z-20 py-2 touch-manipulation"
                >
                  {wishlistIds.includes(product._id || product.id) ? (
                    <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5V1.5V3Z" fill="white" stroke="black"/>
                    </svg>
                  )}
                </button>
              )}

              {/* Left: Title & Controls */}
              <div className="flex-1 w-full md:w-auto">
                <div className="flex flex-col justify-between h-full text-center md:text-left">
                  <h2 className=" text-2xl md:text-5xl font-semibold w-full md:w-[85%] lg:w-[92%] mx-auto md:mx-0 leading-[1.02]">
                    {product.product || product.name}
                  </h2>
                  
                  {/* Mobile price */}
                  <span className="md:hidden mt-4 text-sm font-medium hover:text-gray-600 transition-colors">
                    {formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}
                  </span>
                  
                  {/* Middle screen: Price + Shop Now together */}
                  <div className="hidden md:flex lg:hidden flex-col gap-3 mt-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
                      <span className="text-gray-300">|</span>
                      <button 
                        onClick={() => activateProduct(product.slug || product._id || product.id)}
                        className="home-body-text text-sm font-medium hover:text-gray-600 transition-colors bg-transparent border-none cursor-pointer p-0"
                      >
                        Shop now
                      </button>
                    </div>
                  </div>

                  {/* Large screen: Shop Now button */}
                  <button 
                    onClick={() => activateProduct(product.slug || product._id || product.id)}
                    className="hidden lg:block home-body-text text-base font-medium hover:text-gray-600 transition-colors bg-transparent border-none cursor-pointer p-0 self-center lg:self-start mt-4 lg:mt-0"
                  >
                    Shop now
                  </button>
                </div>
              </div>
              
              {/* Right: Image (middle & large screens) */}
              <div className="flex-1 flex justify-end md:justify-end relative w-full">
                <div
                  className="relative w-full md:w-[45%] lg:w-[35%] bg-white flex items-center justify-center aspect-[3/4] overflow-hidden cursor-pointer"
                  onClick={() => activateProduct(product.slug || product._id || product.id)}
                >
                  {/* Heart button - middle screen above image */}
                  {isStore && (
                    <button
                      type="button"
                      aria-label={wishlistIds.includes(product._id || product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                      onPointerUp={(e) => activateWishlist(product._id || product.id, e)}
                      onClick={(e) => activateWishlist(product._id || product.id, e)}
                      className="hidden md:block lg:hidden absolute top-2 right-2 z-20 py-2 touch-manipulation"
                    >
                      {wishlistIds.includes(product._id || product.id) ? (
                        <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717"/>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="white" stroke="black"/>
                        </svg>
                      )}
                    </button>
                  )}

                  <img
                    src={getOptimizedUrl((product.images && product.images[0]) || product.imageUrl || '', 800)}
                    alt={product.product || product.name}
                    className="w-full h-full object-cover bg-white"
                  />

                  {/* Mobile heart button */}
                  <button
                    type="button"
                    aria-label={wishlistIds.includes(product._id || product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                    onPointerUp={(e) => activateWishlist(product._id || product.id, e)}
                    onClick={(e) => activateWishlist(product._id || product.id, e)}
                    className="absolute bottom-2 right-2 z-20 p-2 md:hidden rounded-full shadow-sm touch-manipulation"
                  >
                    {wishlistIds.includes(product._id || product.id) ? (
                      <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="white" stroke="black"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Large screen price */}
              <div className="hidden lg:flex flex-1 text-center items-end font-medium justify-center lg:justify-end w-full lg:w-auto">
                <span className="text-sm hover:text-gray-600 transition-colors">{formatPrice(product.discountedPrice ?? product.mrp ?? product.price ?? 0)}</span>
              </div>

              {/* Mobile shop now button */}
              <div className="md:hidden mt-3 flex items-center justify-center gap-4">
                <button 
                  onClick={() => activateProduct(product.slug || product._id || product.id)}
                  className="home-body-text text-base font-medium hover:text-gray-600 transition-colors bg-transparent border-none cursor-pointer p-0"
                >
                  Shop now
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- Pagination Controls --- */}
      {isStore && totalPages > 1 && (
        <div className="py-8 flex justify-center items-center gap-3 mx-6 font-bdogrotesk">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center transition-colors border border-gray-300 rounded-full md:border-none md:rounded-none hover:bg-gray-100 md:hover:bg-transparent disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft />
          </button>
          
          <div className="flex items-center gap-3">
            {Array.from({ length: Math.min(totalPages, 4) }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`text-base font-medium transition-colors hover:text-gray-600 ${
                  currentPage === page
                    ? 'text-black underline underline-offset-4'
                    : 'text-gray-400'
                }`}
              >
                {page}
              </button>
            ))}
            
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className="text-gray-400 text-base">...</span>
            )}
            
            {totalPages > 4 && (
              <button
                onClick={() => goToPage(totalPages)}
                className={`text-base font-medium transition-colors hover:text-gray-600 ${
                  currentPage === totalPages
                    ? 'text-black underline underline-offset-4'
                    : 'text-gray-400'
                }`}
              >
                {totalPages}
              </button>
            )}
          </div>
          
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center transition-colors border border-gray-300 rounded-full md:border-none md:rounded-none hover:bg-gray-100 md:hover:bg-transparent disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowRight />
          </button>
        </div>
      )}

      {/* --- View All Link --- */}
      {!isStore && (
        <div className={`pb-8 flex justify-center md:justify-end items-center md:pr-8 ${view === 'list' ? 'pt-8 md:pt-10' : ''}`}>
          <Link 
            to="/store" 
            className="home-body-text flex items-center space-x-3 text-sm md:text-lg font-medium text-black hover:underline hover:underline-offset-8 hover:decoration-2 transition-all group"
          >
            <span>View all</span>
            <span className="hidden md:inline"><ArrowRight /></span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ProductDisplay;